import {
	BiconomySmartAccountV2,
	BuildUserOpOptions,
	DEFAULT_ENTRYPOINT_ADDRESS,
} from '@biconomy/account'
import { Bundler } from '@biconomy/bundler'
import { Transaction } from '@biconomy/core-types'
import { MultiChainValidationModule, SessionKeyManagerModule } from '@biconomy/modules'
import { IHybridPaymaster, PaymasterMode, SponsorUserOperationDto } from '@biconomy/paymaster'
import { TransactionRequest } from '@ethersproject/providers'
import { BLAST_NETWORK_LOOKUP } from '@kwenta/sdk/constants'
import { Signer, Wallet } from 'ethers'
import { defaultAbiCoder } from 'ethers/lib/utils'
import { StorageKeys } from 'services/storage/storageKeys'
import { BaseStorage } from 'services/storage/types'
import { Address } from 'wagmi'

import {
	BICONOMY_SESSION_KEY_MODULE_ADDRESS,
	BICONOMY_VALIDATION_MODULE_ADDRESS,
} from 'constants/address'
import { DateInterval, convertIntervalToSeconds } from 'utils/dates'
import logError from 'utils/logError'
import Notifier from 'utils/notifier'

export class AccountAbstraction extends Notifier<AccountAbstraction> {
	private sdk?: BiconomySmartAccountV2

	private validationModule?: MultiChainValidationModule
	private sessionKeyManagerModule?: SessionKeyManagerModule

	private readonly MULTICHAIN_MODULE_ADDRESS = '0x000000824dc138db84FD9109fc154bdad332Aa8E'

	public accountAddress?: Address
	private _chainId?: number
	private sessionKeyModuleAddress?: Address
	private validationModuleAddress?: Address

	private storage: BaseStorage

	constructor(storage: BaseStorage) {
		super()

		this.storage = storage
	}

	public get chainId() {
		return this._chainId
	}

	private set chainId(chainId: number | undefined) {
		this._chainId = chainId
	}

	public async init(signer: Signer) {
		const validationModule = await this.initValidationModule(signer)

		const chainId = await signer.getChainId()

		this.chainId = chainId

		this.validationModuleAddress = BICONOMY_VALIDATION_MODULE_ADDRESS[chainId]
		this.sessionKeyModuleAddress = BICONOMY_SESSION_KEY_MODULE_ADDRESS[chainId]

		const bundler: Bundler = new Bundler({
			bundlerUrl: `https://bundler.biconomy.io/api/v2/${chainId}/${process.env.NEXT_PUBLIC_BICONOMY_BUNDLER_API_KEY}`,
			chainId,
			entryPointAddress: DEFAULT_ENTRYPOINT_ADDRESS,
		})

		const rpcUrl = BLAST_NETWORK_LOOKUP[chainId]
			? `https://${BLAST_NETWORK_LOOKUP[chainId]}.blastapi.io/${process.env.NEXT_PUBLIC_BLASTAPI_PROJECT_ID}`
			: undefined

		this.sdk = await BiconomySmartAccountV2.create({
			chainId,
			bundler,
			entryPointAddress: DEFAULT_ENTRYPOINT_ADDRESS,
			defaultValidationModule: validationModule,
			activeValidationModule: validationModule,
			rpcUrl,
		})

		this.accountAddress = (await this.sdk.getAccountAddress()) as Address

		if (this.sessionKeyManagerModule) {
			await this.getOrCreateSessionKeyManagerModule(true) // update session module with new key
		}

		this.notifyAll()
	}

	public disconnect() {
		this.sdk = undefined
		this.accountAddress = undefined
		this.chainId = undefined

		this.notifyAll()
	}

	private async initValidationModule(signer: Signer) {
		this.validationModule = await MultiChainValidationModule.create({
			signer,
			moduleAddress: this.MULTICHAIN_MODULE_ADDRESS,
		})

		return this.validationModule
	}

	public async checkSessionKeyModuleEnabled() {
		if (!this.sdk || !this.sessionKeyModuleAddress) {
			throw new Error('SDK not initialized')
		}

		try {
			const sessionKeyModuleEnabled = await this.sdk.isModuleEnabled(this.sessionKeyModuleAddress)

			return sessionKeyModuleEnabled
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(
				`Error checking session key module enabled, check ${this.sessionKeyModuleAddress} for account ${this.accountAddress}`
			)
			logError(e)
			return false
		}
	}

	private async getOrCreateSessionKeyManagerModule(create = false) {
		if (this.sessionKeyModuleAddress === undefined || this.accountAddress === undefined) {
			throw new Error('SDK not initialized')
		}

		if (!this.sessionKeyManagerModule || create) {
			this.sessionKeyManagerModule = await SessionKeyManagerModule.create({
				moduleAddress: this.sessionKeyModuleAddress,
				smartAccountAddress: this.accountAddress,
			})
		}

		return this.sessionKeyManagerModule
	}

	public async createSession(interval: DateInterval | number, smAddress: string) {
		if (
			!this.sdk ||
			!this.accountAddress ||
			!this.validationModuleAddress ||
			!this.sessionKeyModuleAddress
		) {
			throw new Error('SDK not initialized')
		}

		try {
			const savedKey = this.storage.get(StorageKeys.SESSION_KEY)
			const signer = savedKey ? new Wallet(savedKey) : Wallet.createRandom()

			if (!savedKey) {
				this.storage.save(StorageKeys.SESSION_KEY, signer.privateKey)
			}

			const signerAddress = await signer.getAddress()

			const sessionModule = await this.getOrCreateSessionKeyManagerModule()

			const KWENTA_EXECUTE_SELECTOR = '0x608d60eb'
			const sessionKeyData = defaultAbiCoder.encode(
				['address', 'address', 'bytes4'],
				[signerAddress, smAddress, KWENTA_EXECUTE_SELECTOR]
			)

			const now = Math.floor(Date.now() / 1000)
			const secInterval =
				typeof interval === 'number' ? interval : convertIntervalToSeconds(interval)
			const getFutureTimestamp = now + secInterval

			await sessionModule.clearPendingSessions()

			const sessionData = await sessionModule.createSessionData([
				{
					validUntil: getFutureTimestamp,
					validAfter: now,
					sessionValidationModule: this.validationModuleAddress,
					sessionPublicKey: signerAddress,
					sessionKeyData,
				},
			])

			const enableTx: Transaction = {
				to: this.sessionKeyModuleAddress,
				data: sessionData.data,
			}

			const isSessionKeyModuleEnabled = await this.checkSessionKeyModuleEnabled()

			const transactions: Transaction[] = []

			if (!isSessionKeyModuleEnabled) {
				const initTx = await this.sdk.getEnableModuleData(this.sessionKeyModuleAddress)

				transactions.push(initTx)
			}

			transactions.push(enableTx)

			await this.sendUserOperations(transactions, {})

			this.sdk = this.sdk.setActiveValidationModule(sessionModule)

			return this.getSessionInfo()
		} catch (e) {
			logError(e)
			this.removeLocalSessions()
			throw new Error(e)
		}
	}

	public async getSessionInfo() {
		if (this.sdk === undefined || this.accountAddress === undefined) {
			throw new Error('SDK not initialized')
		}

		try {
			const privateKey = this.storage.get(StorageKeys.SESSION_KEY)

			if (!privateKey) {
				throw new Error('Session key not found')
			}

			const signer = new Wallet(privateKey)
			const signerAddress = await signer.getAddress()

			const sessionModule = await this.getOrCreateSessionKeyManagerModule()

			const sessions = await sessionModule.sessionStorageClient.getAllSessionData({
				sessionValidationModule: this.validationModuleAddress,
				sessionPublicKey: signerAddress,
			})

			const session = sessions.reduce(
				(max, current) => (current.validUntil > max.validUntil ? current : max),
				sessions[0]
			)

			return session
		} catch (e) {
			logError(e)
			return undefined
		}
	}

	public removeLocalSessions() {
		if (!this.accountAddress) {
			return
		}

		// TODO: Remove this when Biconomy will fix their bug
		localStorage.setItem(
			`${this.accountAddress.toLowerCase()}_sessions`,
			JSON.stringify({ merkleRoot: '', leafNodes: [] })
		)
		this.sessionKeyManagerModule = undefined
		this.sdk = this.sdk?.setActiveValidationModule(this.validationModule!)
	}

	public async closeAllSessions() {
		if (!this.sdk || !this.accountAddress || !this.sessionKeyModuleAddress) {
			throw new Error('SDK not initialized')
		}

		try {
			const sessionModule = await this.getOrCreateSessionKeyManagerModule()

			const { data } = await sessionModule.createSessionData([])

			const transactions: Transaction[] = [
				{
					to: this.sessionKeyModuleAddress,
					data,
				},
			]

			await this.sendUserOperations(transactions, {})

			this.removeLocalSessions()

			return this.getSessionInfo()
		} catch (e) {
			logError(e)
			throw new Error(e)
		}
	}

	public async sendUserOperations(
		transactions: Array<Transaction | TransactionRequest>,
		options?: BuildUserOpOptions
	) {
		if (!this.sdk) {
			throw new Error('SDK not initialized')
		}

		if (transactions.length === 0) {
			throw new Error('No transactions to send')
		}

		try {
			const opts = options ?? (await this.prepareSessionOptions())
			if (options) {
				this.sdk = this.sdk.setActiveValidationModule(this.validationModule!)
			} else {
				this.sdk = this.sdk.setActiveValidationModule(this.sessionKeyManagerModule!)
			}

			let partialUserOp = await this.sdk.buildUserOp(transactions as Transaction[], opts)

			if (this.sdk.paymaster) {
				const paymaster = this.sdk.paymaster as IHybridPaymaster<SponsorUserOperationDto>

				try {
					const paymasterServiceData: SponsorUserOperationDto = {
						mode: PaymasterMode.SPONSORED,
						smartAccountInfo: {
							name: 'BICONOMY',
							version: '2.0.0',
						},
					}

					const { callGasLimit, verificationGasLimit, preVerificationGas, paymasterAndData } =
						await paymaster.getPaymasterAndData(partialUserOp, paymasterServiceData)

					if (callGasLimit && verificationGasLimit && preVerificationGas) {
						partialUserOp = {
							...partialUserOp,
							paymasterAndData,
							callGasLimit,
							verificationGasLimit,
							preVerificationGas,
						}
					}
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('Paymaster failed, trying without paymaster')
				}
			}

			const response = await this.sdk.sendUserOp(partialUserOp, opts?.params)

			const txDetails = await response.wait()

			return txDetails.receipt
		} catch (e) {
			logError(e)
			switch (e.message) {
				case "AA21 didn't pay prefund":
					throw new Error(
						'One-Click Trading balance is low. Please, fund your wallet and try again!'
					)
				case 'AA23 reverted: SessionNotApproved':
					this.removeLocalSessions()
					throw new Error('Session invalid. Try reopening the session to continue')
				default:
					throw new Error(e.message)
			}
		}
	}

	public async prepareSessionOptions(): Promise<BuildUserOpOptions> {
		if (!this.sdk) {
			throw new Error('SDK not initialized')
		}

		const privateKey = this.storage.get(StorageKeys.SESSION_KEY)

		if (!privateKey) {
			throw new Error('Session key not found')
		}

		const sessionSigner = new Wallet(privateKey)

		const params = {
			sessionSigner,
			sessionValidationModule: this.validationModuleAddress,
		}

		return {
			skipBundlerGasEstimation: false,
			params,
		}
	}
}
